import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import { AccountMenu } from "tsi-common-react/src/apps/authentication/components/AccountMenu";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/containers/BasketMenuIcon";
import { RichNavigation } from "tsi-common-react/src/apps/rich-navigation/RichNavigation";
import { ValueProps } from "tsi-common-react/src/apps/rich-navigation/RichNavigationLinkCollection";
import Link from "tsi-common-react/src/common/Link";
import { Messages } from "tsi-common-react/src/common/Message";
import { RichText } from "tsi-common-react/src/common/RichText";
import { SVGIcon } from "tsi-common-react/src/common/SVGIcon";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { strToBool } from "tsi-common-react/src/utils/format";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import {
    getPageSetting,
    isPageInsideIFrame as isPageInsideIFrameCheck,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { reverse, templates } from "@reactivated";

import { FinancingModalTriggerMinicart } from "../src/Checkout/components/FinancingModalTriggerMinicart";
import { MainMenu } from "../src/Common/components/MainMenu";
import { SearchNav } from "../src/Common/components/SearchNav";
import { IMenuItem } from "../src/Common/menu.interface";
import { store } from "../src/store";

import styles from "./SiteHeaderTemplate.module.scss";

interface ILogoVariants {
    [key: string]: {
        title: string;
        linkClassName: string;
        ariaLabel: string;
        logoClassName?: string;
        logoSvgClassName?: string;
        logoName: string;
        logoTitle: string;
        role?: string;
        target?: string;
    };
}

// TODO(i18n): All title, ariaLabel, and logoTitle values should be wrapped in t tagged templates for translation
export const logoVariants: ILogoVariants = {
    tempurpedic: {
        title: "TempurPedic",
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: "Tempur-Pedic",
        logoClassName: "site-nav__logo",
        logoName: "tempurpedicLogo",
        logoTitle: "Tempur-Pedic",
    },
    sealywarranty: {
        title: "Sealy",
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: "Sealy",
        logoClassName: "sealy-logo",
        logoSvgClassName: "sealy-logo",
        logoName: "sealyLogo",
        logoTitle: "Sealy Logo",
    },
    sealyemployees: {
        title: "Sealy | Stearns & Foster",
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: "Sealy | Stearns & Foster",
        logoSvgClassName: "logo-alt",
        logoName: "sealyStearnsLogo",
        logoTitle: "Sealy Logo",
    },
    sealyretail: {
        title: "Sealy | Stearns & Foster",
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: "Sealy | Stearns & Foster",
        logoSvgClassName: "logo-alt",
        logoName: "sealyStearnsLogo",
        logoTitle: "Sealy | Stearns & Foster",
    },
    stearnswarranty: {
        title: "Stearns & Foster",
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: "Stearns & Foster",
        logoSvgClassName: "site-nav__logo stearns-logo",
        logoName: "stearnsWarrantyLogo",
        logoTitle: "Stearns & Foster",
    },
};

const getLogoVariants = (isSSR: boolean) => {
    const appSlugName = !isSSR && getPageSetting("app-slug");
    // defaults can be removed and added to variant options if needed
    const defaults = {
        role: "listitem",
        target: "_top",
    };
    // If any sub-sites which will use tempurpedic logo or its own logo,
    // should be added here otherwise it will return null
    switch (appSlugName) {
        case "tempurwarranty":
        case "employees":
        case "friends":
        case "associates":
        case "sale2":
        case "tempurpedic":
            return { ...defaults, ...logoVariants.tempurpedic };
        case "sealywarranty":
            return { ...defaults, ...logoVariants.sealywarranty };
        case "sealyemployees":
            return { ...defaults, ...logoVariants.sealyemployees };
        case "sealyretail":
            return { ...defaults, ...logoVariants.sealyretail };
        case "stearnswarranty":
            return { ...defaults, ...logoVariants.stearnswarranty };
        default:
            // Prevent out of context logo rendering and flashing effect
            return null;
    }
};

export const Template = (props: templates.SiteHeaderTemplate) => {
    const isSSR = useIsSSR();
    const viewport = useCurrentBreakpoint();
    const [mobilePanelVisible, setMobilePanelVisible] = useState(false);
    const [dropdownOffsetTop, setDropdownOffsetTop] = useState(0);
    const siteHeaderRef = useRef<HTMLElement | null>(null);
    const logoRef = useRef<HTMLDivElement | null>(null);
    const {
        promo_settings,
        messages,
        rich_navigation,
        show_admin_tools,
        user,
        access_admin,
        top_nav_menu,
    } = props;

    const homeURL = usePageURL("home");
    const dashboard = isoWebPageURL.wrap(reverse("dashboard:index"));
    const wagtailAdminHome = isoWebPageURL.wrap(reverse("wagtailadmin_home"));
    const enableBasketPrequalSDK =
        !isSSR && strToBool(getPageSetting("show-prequal-cart") as string);
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();
    const enableStoreSearch =
        !isSSR &&
        strToBool(getPageSetting("retail-locator-display-enabled") as string);

    // Map top_nav_menu data to IMenuItem[]
    const menuItems: IMenuItem[] = Array.isArray(top_nav_menu.menu.value)
        ? (top_nav_menu.menu.value as IMenuItem[])
        : [];

    // Render logo for tempurpedic and sub-sites
    const logoVariant = getLogoVariants(isSSR);

    const toggleMobilePanel = () => {
        if (viewport.belowMobile) {
            setMobilePanelVisible((o) => !o);
        }
    };

    useEffect(() => {
        if (isSSR) return;
        const resizeHandler = () => {
            if (window.innerWidth >= 768) {
                // Close mobile panel if viewport is resized above mobile breakpoint
                // Remove modal-open class to remove styling effects
                document.body.classList.remove("modal-open");
                setMobilePanelVisible(false);
            }
        };
        // Initialize rich navigation resize handler
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        return () => {
            // Cleanup rich navigation resize handler
            window.removeEventListener("resize", resizeHandler);
        };
    }, [isSSR]);

    useEffect(() => {
        if (isSSR) return;
        if (!logoRef.current) return;

        const headerElem = siteHeaderRef.current;
        const logoElem = logoRef.current?.querySelector("svg");

        if (headerElem && logoElem) {
            // Get scroll position
            const getScrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            // Calculate dropdown offset
            const headerBottom = headerElem.getBoundingClientRect().bottom;
            setDropdownOffsetTop(headerBottom + getScrollTop);
        } else {
            // TODO(i18n): Wrap console warning in t tagged template: t`Header or Logo element not found during offset calculation.`
            console.warn(
                "Header or Logo element not found during offset calculation.",
            );
        }
    }, [isSSR, siteHeaderRef, logoRef]);

    return (
        <>
            {promo_settings.show_promo_header && (
                <nav className="search-nav" role="search">
                    <div className="l-max-width">
                        <a
                            className="screen-reader-skip"
                            href="#main-content"
                        >{t`Skip to main content`}</a>
                        {promo_settings.promo_header?.value.map((promo, i) => (
                            <React.Fragment key={`key:${i}`}>
                                {promo.type === "promo" && (
                                    <>
                                        <div className="search-nav__promo">
                                            {promo.value.copy && (
                                                <RichText
                                                    html={promo.value.copy}
                                                />
                                            )}
                                        </div>
                                        <div className="search-nav__promo--mobile">
                                            {promo.value.mobile_copy ? (
                                                <RichText
                                                    html={
                                                        promo.value.mobile_copy
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    {promo.value.copy && (
                                                        <RichText
                                                            html={
                                                                promo.value.copy
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                        <div className="search-nav__links">
                            <Provider store={store}>
                                <SearchNav
                                    enableStoreSearch={enableStoreSearch}
                                />
                            </Provider>
                        </div>
                    </div>
                </nav>
            )}

            {messages && <Messages messages={messages} />}

            <header className="site-header" ref={siteHeaderRef}>
                <nav className="site-nav l-max-width">
                    <div>
                        {rich_navigation && (
                            <button
                                className="mobile-toggle"
                                onClick={toggleMobilePanel}
                            >
                                {mobilePanelVisible ? (
                                    <SVGIcon name="xIcon" alt={t`Close icon`} />
                                ) : (
                                    <SVGIcon
                                        name="barsIcon"
                                        alt={t`Hamburger navigation icon`}
                                    />
                                )}
                            </button>
                        )}
                        <h1>
                            {logoVariant && (
                                <Link
                                    title={logoVariant.title}
                                    className={logoVariant.linkClassName}
                                    href={homeURL}
                                    aria-label={logoVariant.ariaLabel}
                                    role={logoVariant.role}
                                    target={logoVariant.target}
                                >
                                    <div ref={logoRef}>
                                        <SVGIcon
                                            name={logoVariant.logoName}
                                            className={
                                                logoVariant.logoClassName
                                            }
                                            svgClassName={
                                                logoVariant.logoSvgClassName
                                            }
                                            alt={logoVariant.logoTitle}
                                        />
                                    </div>
                                </Link>
                            )}
                        </h1>
                        <nav
                            className={classNames({
                                [styles.mainMenuContainer]: true,
                                "rich-navigation--active": !!rich_navigation,
                            })}
                        >
                            <MainMenu
                                menuItems={menuItems}
                                dropdownOffsetTop={dropdownOffsetTop}
                            />
                        </nav>
                    </div>

                    <ul>
                        <li>
                            <Provider store={store}>
                                <AccountMenu />
                            </Provider>
                        </li>
                        {/** Do not add admin navigations on mobile - Can be changed upon request */}
                        {show_admin_tools && !viewport.belowMobile && (
                            <>
                                {user?.is_staff && (
                                    <li>
                                        {/* TODO(i18n): Wrap title attribute in t tagged template: title={t`Dashboard`} */}
                                        <Link
                                            href={dashboard}
                                            title="Dashboard"
                                        >
                                            {t`Dashboard`}
                                        </Link>
                                    </li>
                                )}
                                {access_admin && (
                                    <li>
                                        {/* TODO(i18n): Wrap title attribute in t tagged template: title={t`CMS`} */}
                                        <Link
                                            href={wagtailAdminHome}
                                            title="CMS"
                                        >
                                            {t`CMS`}
                                        </Link>
                                    </li>
                                )}
                            </>
                        )}
                        <li>
                            <Provider store={store}>
                                <BasketMenuIcon
                                    basketURL={basketURL}
                                    checkoutURL={checkoutURL}
                                    isPageInsideIFrame={isPageInsideIFrame}
                                    basketLineVariant={
                                        BasketLineVariant.MINIMAL_ENHANCED
                                    }
                                    buildPromoComponents={(closeModal) => {
                                        if (!enableBasketPrequalSDK) {
                                            return null;
                                        }
                                        return (
                                            <Provider store={store}>
                                                <FinancingModalTriggerMinicart
                                                    closeModal={closeModal}
                                                />
                                            </Provider>
                                        );
                                    }}
                                    isSSR={isSSR}
                                />
                            </Provider>
                        </li>
                    </ul>
                </nav>

                {mobilePanelVisible &&
                    rich_navigation &&
                    viewport.belowMobile && (
                        <nav
                            id="mobile-panel"
                            className="rich-navigation"
                            aria-hidden={!mobilePanelVisible}
                        >
                            <RichNavigation
                                openMobileContentDefaultState={true}
                                {...rich_navigation}
                            />
                            <ValueProps
                                value_props={rich_navigation.value_props}
                                enableValuePropsOnMobile
                            />
                        </nav>
                    )}
            </header>
        </>
    );
};
